/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  isAnyOf
} from '@reduxjs/toolkit'
import { userAccountInfo } from 'helpers/user'
import { fetchAccountSummary } from 'redux/accountSummary/accountSummarySlice'
import type { OriginalRootState } from 'redux/rootReducer'
import autoPayAccountsApi from 'services/autoPay'
import {
  AutoPayEnrollRequest,
  AutoPayPaymentOption,
  AutoPayStatusRequest,
  AutoPayStatusResponse,
  BankChannel,
  PaymentCardInfo,
  PaymentMedium
} from 'services/autoPay/typings'
import { ServerError } from 'typings/serverError'

export interface AutoPayStatusState {
  isLoading: boolean
  isAutoPayUpdated: boolean
  isAutoPayCancelled: boolean
  response: AutoPayStatusResponse
  error: ServerError
}

export const initState: AutoPayStatusState = {
  isLoading: false,
  isAutoPayUpdated: false,
  isAutoPayCancelled: false,
  response: {
    enrollment: false,
    paymentMedium: PaymentMedium.ECheck,
    enrollmentDetails: {
      paymentOption: AutoPayPaymentOption.MinimumPaymentDue,
      paymentAmount: '',
      nextDueDate: '',
      effectiveCurrentCycle: false
    },
    paymentMethod: {
      bankAccountNumberLast4: '',
      bankRoutingNumber: '',
      type: '',
      id: '',
      name: '',
      nickname: '',
      bankChannel: BankChannel.Digital,
      status: ''
    },
    paymentCardInfo: {} as PaymentCardInfo
  },
  error: null
}

export const fetchAutoPayStatus = createAsyncThunk(
  'autoPayStatusSlice/fetchAutoPayStatus',
  async (autoPayStatusRequest: AutoPayStatusRequest) => {
    return autoPayAccountsApi.getStatus(autoPayStatusRequest)
  }
)

export const putAutoPayStatus = createAsyncThunk(
  'autoPayStatusSlice/putAutoPayStatus',
  async (autoPayEnrollRequest: AutoPayEnrollRequest, { dispatch }) => {
    const response = await autoPayAccountsApi.putEnroll(autoPayEnrollRequest)

    const {
      requestBody: { enrollment, accountIdType, 'cipher.accountId': accountID },
      accessToken
    } = autoPayEnrollRequest

    if (enrollment) {
      dispatch(updateIsAutoPayUpdated())
    } else {
      dispatch(updateIsAutoPayCanceled())
      // need to update account summary state with billing changes
      await dispatch(
        fetchAccountSummary({
          accountId: accountID,
          accountIdType,
          accessToken
        })
      )
    }

    return response
  }
)

interface EnrollInAutoPayPayload {
  paymentOption: AutoPayPaymentOption
  paymentAmount: string
}

export const enrollInAutoPay = createAsyncThunk<
  AutoPayStatusResponse,
  EnrollInAutoPayPayload,
  { state: OriginalRootState }
>(
  'autoPayStatusSlice/enrollInAutoPay',
  async ({ paymentOption, paymentAmount }, { getState, dispatch }) => {
    const { payment, authTokenInfo } = getState()
    const accessToken = authTokenInfo.tokenInfo.access_token
    const { accountID, accountIdType } = userAccountInfo

    const response = await autoPayAccountsApi.putEnroll({
      accessToken,
      requestBody: {
        'cipher.accountId': accountID,
        accountIdType,
        enrollment: true,
        paymentOption,
        paymentAmount,
        paymentMethod: {
          id: payment.selectedPayment.value
        }
      }
    })

    // need to update account summary state with billing changes
    await dispatch(
      fetchAccountSummary({
        accountId: accountID,
        accountIdType,
        accessToken
      })
    )
    return response
  }
)

export const autoPayStatusSlice = createSlice({
  name: 'autoPayStatusSlice',
  initialState: initState,
  reducers: {
    updateIsAutoPayUpdated: (state: AutoPayStatusState) => {
      state.isAutoPayUpdated = true
    },
    updateIsAutoPayCanceled: (state: AutoPayStatusState) => {
      state.isAutoPayCancelled = true
    },
    resetAutoPayStatus: (state: AutoPayStatusState) => {
      state.isAutoPayUpdated = false
      state.isAutoPayCancelled = false
    }
  },
  extraReducers: builder => {
    builder
      .addCase(enrollInAutoPay.fulfilled, (state, action) => {
        state.isLoading = false
        state.response = {
          ...state.response,
          ...action.payload
        }
        state.isAutoPayUpdated = true
      })
      .addMatcher(
        isAnyOf(
          fetchAutoPayStatus.pending,
          putAutoPayStatus.pending,
          enrollInAutoPay.pending
        ),
        (state: AutoPayStatusState) => {
          state.isLoading = true
          state.error = null
        }
      )
      .addMatcher(
        isAnyOf(fetchAutoPayStatus.fulfilled, putAutoPayStatus.fulfilled),
        (
          state: AutoPayStatusState,
          action: PayloadAction<AutoPayStatusResponse>
        ) => {
          state.isLoading = false
          state.response = {
            ...state.response,
            ...action.payload
          }
        }
      )
      .addMatcher(
        isAnyOf(
          fetchAutoPayStatus.rejected,
          putAutoPayStatus.rejected,
          enrollInAutoPay.rejected
        ),
        (state: AutoPayStatusState, action: PayloadAction<unknown>) => {
          state.isLoading = false
          state.error = action.payload as ServerError
        }
      )
  }
})

export const {
  updateIsAutoPayUpdated,
  updateIsAutoPayCanceled,
  resetAutoPayStatus
} = autoPayStatusSlice.actions

export default autoPayStatusSlice.reducer

/* SELECTORS */

/**
 * Selects loading status for auto pay request
 */
export const selectIsAutoPayStatusLoading = (state: OriginalRootState) =>
  state.autoPayStatus.isLoading

/**
 * Selects auto pay enrollment details response data
 */
export const selectAutoPayEnrollmentDetails = (state: OriginalRootState) =>
  state.autoPayStatus.response.enrollmentDetails
